import * as React from 'react';
import { createRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ContactCreateRequest, type ClientDetail, type GstTreatment } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api, ApiRequestError } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  GstinLookupButton,
  GstinLookupNote,
  gstinHint,
  placeOfSupplyHint,
  useGstinAutoFill,
  useGstinLookup,
} from '@/components/GstinAssist';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const crmClientDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/clients/$clientId',
  component: ClientDetailPage,
  // Set only by the create flow, so the page can confirm the save once.
  validateSearch: (search: Record<string, unknown>): { saved?: boolean } =>
    search.saved === true || search.saved === 'true' ? { saved: true } : {},
});

type ContactValues = {
  name: string;
  email?: string;
  phone?: string;
  title?: string;
};

type EditValues = {
  legalName: string;
  displayName: string;
  gstin: string;
  gstTreatment: GstTreatment;
  pan: string;
  billingAddress: string;
  placeOfSupplyState: string;
  paymentTermsDays: number;
  leadChannelId: string;
  leadChannelChangeReason: string;
};

type MergeValues = { targetId: string; reason: string };

function editValuesOf(c: ClientDetail): EditValues {
  return {
    legalName: c.legalName,
    displayName: c.displayName,
    gstin: c.gstin ?? '',
    gstTreatment: c.gstTreatment,
    pan: c.pan ?? '',
    billingAddress: c.billingAddress,
    placeOfSupplyState: c.placeOfSupplyState,
    paymentTermsDays: c.paymentTermsDays,
    leadChannelId: c.leadChannelId,
    leadChannelChangeReason: '',
  };
}

function ClientDetailPage(): JSX.Element {
  const { clientId } = crmClientDetailRoute.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { saved } = useSearch({ from: crmClientDetailRoute.id });
  const [showSaved, setShowSaved] = React.useState(false);
  React.useEffect(() => {
    if (!saved) return;
    setShowSaved(true);
    // Drop the flag from the URL so a refresh or a shared link does not repeat it.
    void navigate({
      to: '/crm/clients/$clientId',
      params: { clientId },
      search: {},
      replace: true,
    });
    const timer = window.setTimeout(() => setShowSaved(false), 8000);
    return () => window.clearTimeout(timer);
  }, [saved, clientId, navigate]);
  const [contactOpen, setContactOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [mergeOpen, setMergeOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  const isAdmin = (meQuery.data?.person.roles ?? []).includes('Admin');

  const detailQuery = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => api.getClient(clientId),
  });
  const timelineQuery = useQuery({
    queryKey: ['client-timeline', clientId],
    queryFn: () => api.getClientTimeline(clientId),
  });
  const channelsQuery = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.listLeadChannels(),
  });
  const otherClientsQuery = useQuery({
    queryKey: ['clients-for-merge'],
    queryFn: () => api.listClients({ status: 'active', page: 1, pageSize: 100 }),
    enabled: mergeOpen,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['client', clientId] });
    await queryClient.invalidateQueries({ queryKey: ['clients'] });
    await queryClient.invalidateQueries({ queryKey: ['client-timeline', clientId] });
  };

  const archiveMutation = useMutation({
    mutationFn: () => api.archiveClient(clientId),
    onSuccess: async () => {
      await invalidate();
      await navigate({ to: '/crm/clients' });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const contactForm = useForm<ContactValues>({
    resolver: zodResolver(ContactCreateRequest),
    defaultValues: { name: '' },
  });
  const addContactMutation = useMutation({
    mutationFn: (body: ContactValues) => api.addContact(clientId, body),
    onSuccess: async () => {
      setContactOpen(false);
      contactForm.reset();
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const editForm = useForm<EditValues>();
  const editGstin = editForm.watch('gstin');
  const editPlaceOfSupply = editForm.watch('placeOfSupplyState');
  useGstinAutoFill(editGstin, {
    getValue: (field) => editForm.getValues(field),
    setValue: (field, value) =>
      editForm.setValue(field, value, { shouldValidate: true, shouldDirty: true }),
  });
  const lookup = useGstinLookup((r) => {
    editForm.setValue('legalName', r.legalName, { shouldValidate: true, shouldDirty: true });
    if (r.tradeName) editForm.setValue('displayName', r.tradeName, { shouldDirty: true });
    editForm.setValue('pan', r.pan, { shouldValidate: true, shouldDirty: true });
    editForm.setValue('placeOfSupplyState', r.stateCode, {
      shouldValidate: true,
      shouldDirty: true,
    });
    editForm.setValue('gstTreatment', r.suggestedGstTreatment, { shouldDirty: true });
    if (r.address) editForm.setValue('billingAddress', r.address, { shouldDirty: true });
  });
  const editMutation = useMutation({
    mutationFn: (v: EditValues) => {
      const c = detailQuery.data;
      if (!c) throw new Error('Client not loaded');
      const body: Parameters<typeof api.updateClient>[1] = {};
      if (v.legalName !== c.legalName) body.legalName = v.legalName;
      if (v.displayName !== c.displayName) body.displayName = v.displayName;
      if ((v.gstin || null) !== c.gstin) body.gstin = v.gstin ? v.gstin.toUpperCase() : null;
      if (v.gstTreatment !== c.gstTreatment) body.gstTreatment = v.gstTreatment;
      if ((v.pan || null) !== c.pan) body.pan = v.pan ? v.pan.toUpperCase() : null;
      if (v.billingAddress !== c.billingAddress) body.billingAddress = v.billingAddress;
      if (v.placeOfSupplyState !== c.placeOfSupplyState)
        body.placeOfSupplyState = v.placeOfSupplyState;
      if (Number(v.paymentTermsDays) !== c.paymentTermsDays)
        body.paymentTermsDays = Number(v.paymentTermsDays);
      if (v.leadChannelId !== c.leadChannelId) {
        body.leadChannelId = v.leadChannelId;
        body.leadChannelChangeReason = v.leadChannelChangeReason;
      }
      return api.updateClient(clientId, body);
    },
    onSuccess: async () => {
      setEditOpen(false);
      setErrorMessage(null);
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const mergeForm = useForm<MergeValues>({ defaultValues: { targetId: '', reason: '' } });
  const mergeMutation = useMutation({
    mutationFn: (v: MergeValues) =>
      api.mergeClients({ sourceId: clientId, targetId: v.targetId, reason: v.reason }),
    onSuccess: async (_res, v) => {
      setMergeOpen(false);
      setErrorMessage(null);
      await invalidate();
      await navigate({ to: '/crm/clients/$clientId', params: { clientId: v.targetId } });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  if (detailQuery.isLoading) {
    return (
      <AppShell>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading...</p>
      </AppShell>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <AppShell>
        <Alert variant="destructive">
          <AlertTitle>Could not load client</AlertTitle>
          <AlertDescription>
            {detailQuery.error instanceof ApiRequestError
              ? detailQuery.error.message
              : 'Unexpected error.'}
          </AlertDescription>
        </Alert>
      </AppShell>
    );
  }

  const client = detailQuery.data;
  const channels = channelsQuery.data ?? [];
  const channelName =
    channels.find((c) => c.id === client.leadChannelId)?.name ?? client.leadChannelId;
  const watchedChannel = editForm.watch('leadChannelId');
  const channelChanged =
    editOpen && watchedChannel !== undefined && watchedChannel !== client.leadChannelId;

  return (
    <AppShell>
      {showSaved ? (
        <Alert variant="success" className="flex items-start justify-between gap-4">
          <div>
            <AlertTitle>Client saved</AlertTitle>
            <AlertDescription>
              “{client.displayName}” is now in your client list and can be picked when creating
              projects and documents.
            </AlertDescription>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowSaved(false)}>
            Dismiss
          </Button>
        </Alert>
      ) : null}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Link
            to="/crm/clients"
            className="text-xs font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
          >
            ← Clients
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl font-semibold tracking-tight">
              {client.displayName}
            </h1>
            <Badge variant="muted">{client.status}</Badge>
          </div>
          {client.legalName !== client.displayName ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{client.legalName}</p>
          ) : null}
          {client.mergedIntoId ? (
            <p className="text-sm">
              Merged into{' '}
              <Link
                to="/crm/clients/$clientId"
                params={{ clientId: client.mergedIntoId }}
                className="text-[hsl(var(--brand))] hover:underline"
              >
                another client
              </Link>
              .
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {client.status === 'active' ? (
            <Button
              variant="outline"
              onClick={() => {
                editForm.reset(editValuesOf(client));
                setErrorMessage(null);
                setEditOpen(true);
              }}
            >
              Edit
            </Button>
          ) : null}
          {client.status === 'active' && isAdmin ? (
            <Button
              variant="outline"
              onClick={() => {
                mergeForm.reset({ targetId: '', reason: '' });
                setErrorMessage(null);
                setMergeOpen(true);
              }}
            >
              Merge into…
            </Button>
          ) : null}
          {client.status === 'active' && isAdmin ? (
            <Button
              variant="outline"
              onClick={() => {
                if (window.confirm('Archive this client?')) archiveMutation.mutate();
              }}
              disabled={archiveMutation.isPending}
            >
              Archive
            </Button>
          ) : null}
          <Dialog open={contactOpen} onOpenChange={setContactOpen}>
            <DialogTrigger asChild>
              <Button>Add contact</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add contact</DialogTitle>
                <DialogDescription>People who can be emailed or called.</DialogDescription>
              </DialogHeader>
              <form
                onSubmit={contactForm.handleSubmit((v) => addContactMutation.mutate(v))}
                className="flex flex-col gap-3"
              >
                <Field label="Name" required error={contactForm.formState.errors.name?.message}>
                  <Input {...contactForm.register('name')} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Email" error={contactForm.formState.errors.email?.message}>
                    <Input type="email" {...contactForm.register('email')} />
                  </Field>
                  <Field label="Phone" error={contactForm.formState.errors.phone?.message}>
                    <Input {...contactForm.register('phone')} />
                  </Field>
                  <Field label="Title" error={contactForm.formState.errors.title?.message}>
                    <Input {...contactForm.register('title')} />
                  </Field>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={addContactMutation.isPending}>
                    Save contact
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {errorMessage && !editOpen && !mergeOpen ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>
            Changes are audit-logged. The lead channel is frozen at creation; only an Admin can
            change it, with a reason.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
            <Row label="GSTIN" mono>
              {client.gstin}
            </Row>
            <Row label="GST treatment">
              <span className="capitalize">{client.gstTreatment}</span>
            </Row>
            <Row label="PAN" mono>
              {client.pan}
            </Row>
            <Row label="Place of supply">{client.placeOfSupplyState}</Row>
            <Row label="Payment terms">{client.paymentTermsDays} days</Row>
            <Row label="Lead channel">{channelName}</Row>
            <Row label="Billing address" className="sm:col-span-2 lg:col-span-3">
              <span className="whitespace-pre-line">{client.billingAddress}</span>
            </Row>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contacts</CardTitle>
          <CardDescription>{client.contacts.length} on file</CardDescription>
        </CardHeader>
        <CardContent>
          {client.contacts.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No contacts yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {client.contacts.map((c) => (
                <li
                  key={c.id}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-3 text-sm"
                >
                  <span className="font-medium">{c.name}</span>
                  {c.title ? (
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{c.title}</span>
                  ) : null}
                  <span className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    {c.email ? (
                      <a
                        href={`mailto:${c.email}`}
                        className="text-[hsl(var(--brand))] hover:underline"
                      >
                        {c.email}
                      </a>
                    ) : null}
                    {c.phone ? (
                      <a
                        href={`tel:${c.phone}`}
                        className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                      >
                        {c.phone}
                      </a>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activity</CardTitle>
          <CardDescription>Client changes, documents and projects, newest first.</CardDescription>
        </CardHeader>
        <CardContent>
          {timelineQuery.isPending ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
          ) : (timelineQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No activity yet.</p>
          ) : (
            <ol className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {(timelineQuery.data ?? []).map((e, i) => (
                <li key={i} className="flex items-start gap-3 py-2.5 text-sm">
                  <span className="w-24 shrink-0 text-xs text-[hsl(var(--muted-foreground))]">
                    {e.at.slice(0, 10)}
                  </span>
                  <Badge variant="muted" className="capitalize">
                    {e.kind}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    {e.href ? (
                      <Link
                        to={e.href}
                        className="font-medium text-[hsl(var(--brand))] hover:underline"
                      >
                        {e.title}
                      </Link>
                    ) : (
                      <span className="font-medium capitalize">{e.title}</span>
                    )}
                    {e.detail ? (
                      <span className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">
                        {e.detail}
                      </span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) lookup.reset();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit client</DialogTitle>
            <DialogDescription>GSTIN is checksum-validated on save.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={editForm.handleSubmit((v) => editMutation.mutate(v))}
            className="flex flex-col gap-3"
          >
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Legal name"
                required
                error={editForm.formState.errors.legalName?.message}
              >
                <Input {...editForm.register('legalName', { required: 'Enter the legal name' })} />
              </Field>
              <Field
                label="Display name"
                required
                error={editForm.formState.errors.displayName?.message}
              >
                <Input
                  {...editForm.register('displayName', { required: 'Enter a display name' })}
                />
              </Field>
              <Field label="GST treatment" required>
                <Select {...editForm.register('gstTreatment')}>
                  <option value="registered">Registered</option>
                  <option value="unregistered">Unregistered</option>
                  <option value="composition">Composition</option>
                  <option value="exempt">Exempt</option>
                </Select>
              </Field>
              <Field
                label="GSTIN"
                error={editForm.formState.errors.gstin?.message}
                hint={gstinHint(editGstin)}
              >
                <div className="flex gap-2">
                  <Input
                    className="font-mono uppercase"
                    {...editForm.register('gstin', {
                      validate: (v) =>
                        v === '' || v.trim().length === 15 || 'GSTIN is 15 characters',
                    })}
                  />
                  <GstinLookupButton lookup={lookup} gstin={editGstin} />
                </div>
              </Field>
              <Field label="PAN" error={editForm.formState.errors.pan?.message}>
                <Input
                  className="font-mono uppercase"
                  {...editForm.register('pan', {
                    validate: (v) =>
                      v === '' || /^[A-Za-z]{5}\d{4}[A-Za-z]$/.test(v.trim()) || 'PAN looks wrong',
                  })}
                />
              </Field>
              <Field
                label="Place of supply (state code)"
                required
                error={editForm.formState.errors.placeOfSupplyState?.message}
                hint={placeOfSupplyHint(editPlaceOfSupply)}
              >
                <Input
                  {...editForm.register('placeOfSupplyState', { required: 'Enter the state code' })}
                />
              </Field>
              <Field
                label="Payment terms (days)"
                required
                error={editForm.formState.errors.paymentTermsDays?.message}
              >
                <Input
                  type="number"
                  min="0"
                  max="365"
                  {...editForm.register('paymentTermsDays', {
                    required: 'Enter payment terms',
                    valueAsNumber: true,
                    min: { value: 0, message: 'Cannot be negative' },
                    max: { value: 365, message: 'At most 365 days' },
                  })}
                />
              </Field>
              <Field
                label="Lead channel"
                required
                hint={isAdmin ? undefined : 'Frozen at creation — ask an Admin to change it.'}
              >
                <Select {...editForm.register('leadChannelId')} disabled={!isAdmin}>
                  {channels.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              {channelChanged ? (
                <Field
                  label="Reason for changing the lead channel"
                  required
                  error={editForm.formState.errors.leadChannelChangeReason?.message}
                >
                  <Input
                    {...editForm.register('leadChannelChangeReason', {
                      validate: (v, all) =>
                        all.leadChannelId === client.leadChannelId ||
                        v.trim() !== '' ||
                        'Give a reason',
                    })}
                  />
                </Field>
              ) : null}
            </div>
            <GstinLookupNote lookup={lookup} />
            <Field label="Billing address">
              <Input {...editForm.register('billingAddress')} />
            </Field>
            {errorMessage ? (
              <Alert variant="destructive">
                <AlertTitle>Save failed</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button type="submit" disabled={editMutation.isPending}>
                {editMutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge this client into another</DialogTitle>
            <DialogDescription>
              Contacts, documents and history move to the target. This client is kept as a merged
              record — nothing is deleted.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={mergeForm.handleSubmit((v) => mergeMutation.mutate(v))}
            className="flex flex-col gap-3"
          >
            <Field label="Merge into" required error={mergeForm.formState.errors.targetId?.message}>
              <Select
                {...mergeForm.register('targetId', { required: 'Pick the surviving client' })}
              >
                <option value="">Select…</option>
                {(otherClientsQuery.data?.items ?? [])
                  .filter((c) => c.id !== clientId)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName}
                      {c.gstin ? ` · ${c.gstin}` : ''}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label="Reason" required error={mergeForm.formState.errors.reason?.message}>
              <Input {...mergeForm.register('reason', { required: 'Give a reason' })} />
            </Field>
            {errorMessage ? (
              <Alert variant="destructive">
                <AlertTitle>Merge failed</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button type="submit" variant="destructive" disabled={mergeMutation.isPending}>
                {mergeMutation.isPending ? 'Merging…' : 'Merge'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Row({
  label,
  mono,
  className,
  children,
}: {
  label: string;
  mono?: boolean;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  const empty =
    children === null || children === undefined || children === '' || children === false;
  return (
    <div className={className}>
      <dt className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
        {label}
      </dt>
      <dd
        className={`mt-1 text-sm ${
          empty ? 'text-[hsl(var(--muted-foreground))]' : 'text-[hsl(var(--foreground))]'
        } ${mono && !empty ? 'font-mono' : ''}`}
      >
        {empty ? '—' : children}
      </dd>
    </div>
  );
}
